import { GoogleGenAI, Type } from "@google/genai";
import { TouristSite, AiSearchResult } from "../types";
import { fetchGooglePlaceData } from "./googlePlacesService";
import { searchPhoton } from "./photonService";

export const getAiClient = () => {
  const apiKey = "AIzaSyDMhklsCg3WGlMNeFeScfKxiNpAcSnJrXM";
  return new GoogleGenAI({ apiKey });
};

export async function searchCircuitByAI(
  prompt: string,
  sector: string,
  existingSites: TouristSite[],
  limit: number = 40
): Promise<AiSearchResult> {
  const ai = getAiClient();
  const model = ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: `Tu es un expert logistique et touristique des Cévennes.
Recherche des activités ou lieux touristiques pour le secteur: "${sector}".
Requête client: "${prompt}".
Limite: ${limit} résultats. Tu dois impérativement fournir exactement ${limit} propositions. Si tu n'en trouves pas assez, cherche plus largement dans le secteur pour atteindre le nombre demandé. Ne retourne jamais moins de ${limit} propositions.

IMPORTANT - Tu dois aussi fournir un titre accrocheur pour ce secteur ("sectorTitle") et une description marketing courte pour ce secteur ("sectorDescription").

Respecte strictement les limites géographiques du secteur demandé :
- Vis et Larzac : Cirque de Navacelles, Causse de Blandas, Vallée de la Vis, Saint-Maurice-Navacelles. (N'inclus PAS les Grottes des Demoiselles ou Saint-Guilhem-le-Désert ici).
- Anduze et Cévennes : Anduze, Bambouseraie, Train à vapeur, Saint-Jean-du-Gard, Mialet.
- Gorges de l'Hérault : Grottes des Demoiselles, Saint-Guilhem-le-Désert, Pont du Diable, Lac du Salagou, Saint-Martin-de-Londres.
- Littoral et Camargue : Aigues-Mortes, Le Grau-du-Roi, Sète, Palavas, Étang de Thau.
- Vestiges Romains : Nîmes (Arènes, Maison Carrée), Pont du Gard, Uzès.
- Massif de l'Aigoual : Mont Aigoual, Observatoire, Meyrueis, Dourbies, Valleraugue.

Pour les hébergements, propose UNIQUEMENT des lieux atypiques (cabanes, bulles, yourtes, châteaux, mas isolés). EXCLUS strictement les hôtels classiques, Ibis, Campanile, etc.

Pour chaque lieu trouvé, donne son nom, adresse, et téléphone (si possible).
CRUCIAL : Le nom du lieu ("name") DOIT correspondre EXACTEMENT au nom officiel tel qu'il apparaît sur Google Maps (ex: "Moulin de la Foux" et non "Ancien moulin de navacelles"). C'est indispensable pour que la recherche GPS fonctionne.
Assigne également une ou plusieurs catégories pertinentes parmi la liste suivante (utilise exactement ces identifiants) :
- patrimoine (monuments, musées, histoire)
- nature (parcs, forêts, grottes, paysages)
- activites (sport, loisirs, accrobranche, vélo)
- randonnee (marche, sentiers)
- points_de_vue (panoramas, belvédères)
- terroir (domaines viticoles, fermes, marchés, produits locaux)
- restauration (restaurants, snacks, auberges)
- insolite (lieux atypiques, hébergements insolites, curiosités)
- baignade (rivières, lacs, plages, cascades)
- autre

Un lieu peut avoir plusieurs catégories (ex: un château avec une belle vue = ["patrimoine", "points_de_vue"]).`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          sectorTitle: { type: Type.STRING },
          sectorDescription: { type: Type.STRING },
          propositions: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                name: { type: Type.STRING },
                address: { type: Type.STRING },
                phone: { type: Type.STRING, nullable: true },
                description: { type: Type.STRING },
                categories: { type: Type.ARRAY, items: { type: Type.STRING } }
              },
              required: ["name", "address", "description", "categories"]
            }
          }
        },
        required: ["sectorTitle", "sectorDescription", "propositions"]
      }
    }
  });

  const response = await model;
  if (!response.text) {
    throw new Error("No text in response from Gemini");
  }
  const result = JSON.parse(response.text);
  
  const validatedPropositions = [];
  
  // Traitement par lots de 5
  for (let i = 0; i < result.propositions.length; i += 5) {
    const batch = result.propositions.slice(i, i + 5);
    const batchResults = await Promise.all(batch.map(async (prop: any) => {
      // 1. Essayer d'abord avec Google Places pour le nom, adresse, localisation
      const googleResults = await fetchGooglePlaceData(prop.name, prop.address);
      let siteData: any = {
          name: prop.name,
          address: prop.address,
          location: { lat: 0, lng: 0 }, // Placeholder
          sources: { name: 'ai', address: 'ai', location: 'ai' }
      };
      
      if (googleResults && googleResults.length > 0) {
          siteData = {
              name: googleResults[0].name,
              address: googleResults[0].address || prop.address,
              location: googleResults[0].location || { lat: 0, lng: 0 },
              phone: googleResults[0].phoneNumber || prop.phone,
              website: googleResults[0].website || prop.website,
              openingHours: googleResults[0].openingHours || prop.openingHours,
              sources: {
                  name: 'google',
                  address: googleResults[0].address ? 'google' : 'ai',
                  location: googleResults[0].location ? 'google' : 'ai',
                  phone: googleResults[0].phoneNumber ? 'google' : (prop.phone ? 'ai' : undefined),
                  website: googleResults[0].website ? 'google' : (prop.website ? 'ai' : undefined),
                  openingHours: googleResults[0].openingHours ? 'google' : (prop.openingHours ? 'ai' : undefined)
              }
          };
      } else {
          // Fallback vers Photon (Open Source)
          const photonResults = await searchPhoton(`${prop.name} ${prop.address}`);
          if (photonResults && photonResults.length > 0) {
              siteData = {
                  ...siteData,
                  name: photonResults[0].name,
                  address: photonResults[0].address,
                  location: photonResults[0].location,
                  sources: { ...siteData.sources, name: 'photon', address: 'photon', location: 'photon' }
              };
          }
      }
      
      // Vérifier si existant
      const normalize = (str: string) => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
      const existingSite = existingSites.find(s => 
        s.sector === sector && (normalize(s.name) === normalize(siteData.name) || normalize(s.name) === normalize(prop.name))
      );
      
      return { 
        ...prop, 
        ...siteData,
        isExisting: !!existingSite,
        existingId: existingSite?.id || null
      };
    }));
    
    validatedPropositions.push(...batchResults.filter(Boolean));
    if (validatedPropositions.length >= limit) break;
  }
  
  return { 
    propositions: validatedPropositions.slice(0, limit),
    sectorTitle: result.sectorTitle,
    sectorDescription: result.sectorDescription
  };
}

export async function generatePackContent(sector: string, prompt: string) {
  const ai = getAiClient();
  const model = ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: `Génère un titre accrocheur et un descriptif marketing pour le pack touristique du secteur: "${sector}".
Contexte: ${prompt}`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          description: { type: Type.STRING }
        },
        required: ["title", "description"]
      }
    }
  });

  const response = await model;
  if (!response.text) {
    throw new Error("No text in response from Gemini");
  }
  try {
    return JSON.parse(response.text);
  } catch (e) {
    return null;
  }
}

export async function analyzeActivitiesForClient(questionnaire: any, activities: TouristSite[]) {
  const ai = getAiClient();
  const model = ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: `Analyse ces activités pour ce client:
Questionnaire: ${JSON.stringify(questionnaire)}
Activités: ${JSON.stringify(activities.map(a => ({ id: a.id, name: a.name, categories: a.categories })))}

Attribue un score de 1 à 10 pour chaque activité et une couleur (VERT, JAUNE, ORANGE, ROUGE).`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          properties: {
            id: { type: Type.STRING },
            score: { type: Type.NUMBER },
            color: { type: Type.STRING },
            reason: { type: Type.STRING }
          },
          required: ["id", "score", "color"]
        }
      }
    }
  });

  const response = await model;
  if (!response.text) {
    throw new Error("No text in response from Gemini");
  }
  try {
    return JSON.parse(response.text);
  } catch (e) {
    return [];
  }
}

export async function fetchAIDataForSite(siteName: string, address: string, currentCategories: string[] = [], retries = 3): Promise<any> {
  const ai = getAiClient();
  try {
    const model = ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Tu es un expert touristique et logistique des Cévennes.
Je te donne le nom d'un lieu ou d'une activité: "${siteName}" situé à ou près de "${address}".

Ta mission est de me fournir des informations détaillées et structurées sur ce lieu pour enrichir notre base de données.
Si tu ne connais pas le lieu exact, donne des informations génériques pertinentes pour ce type de lieu dans cette région.

Fournis les informations suivantes:
1.  **description**: Une description courte et accrocheuse (1-2 phrases maximum).
2.  **fullDescription**: Une description détaillée, engageante et informative pour les touristes.
3.  **adultPrice**: Le prix d'entrée estimé pour un adulte en euros (nombre uniquement, ex: 15.5). Si gratuit, mets 0. Si inconnu, laisse vide.
4.  **child6_12Price**: Le prix estimé pour un enfant de 6 à 12 ans.
5.  **child3_6Price**: Le prix estimé pour un enfant de 3 à 6 ans.
6.  **babyPrice**: Le prix estimé pour un bébé (moins de 3 ans). Souvent 0.
7.  **isPaid**: true si l'accès est payant, false sinon.
8.  **openingHours**: Les horaires d'ouverture et périodes d'ouverture dans l'année (ex: "Ouvert d'avril à octobre, 10h-18h").
9.  **dogsNotAllowed**: true si les chiens sont strictement interdits, false s'ils sont acceptés (même en laisse).
10. **isHandicapAccessible**: true si le lieu est accessible aux personnes à mobilité réduite (PMR).
11. **accessibility**: Conditions spécifiques d'accès, conseils saisonniers (ex: "Prévoir un pull, température 14°C", "Chaussures de marche recommandées", "Poussettes déconseillées").
12. **phone**: Numéro de téléphone de contact (si connu).
13. **website**: Site internet officiel (si connu).
14. **address**: Adresse postale formatée (si tu as une meilleure précision).
15. **gps**: Coordonnées GPS au format "latitude, longitude" (ex: "43.123, 3.456").
16. **googleRating**: Une note sur 5 estimée (si tu la connais).
17. **googleReviewsCount**: Un nombre d'avis estimé.
18. **forks**: Si c'est un restaurant, le nombre de fourchettes (0, 1, 2 ou 3). 0: Snack/Restauration rapide/Casse-croûte, 1: Abordable, 2: Moyen, 3: Gastronomique/Cher.
19. **durationMinutes**: La durée estimée de la visite ou de l'activité en minutes (nombre entier). Ex: 15 pour un point de vue, 60 pour un musée, 120 pour une randonnée moyenne.`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            description: { type: Type.STRING },
            fullDescription: { type: Type.STRING },
            adultPrice: { type: Type.NUMBER },
            child6_12Price: { type: Type.NUMBER },
            child3_6Price: { type: Type.NUMBER },
            babyPrice: { type: Type.NUMBER },
            isPaid: { type: Type.BOOLEAN },
            openingHours: { type: Type.STRING },
            dogsNotAllowed: { type: Type.BOOLEAN },
            isHandicapAccessible: { type: Type.BOOLEAN },
            accessibility: { type: Type.STRING },
            phone: { type: Type.STRING },
            website: { type: Type.STRING },
            address: { type: Type.STRING },
            gps: { type: Type.STRING },
            googleRating: { type: Type.NUMBER },
            googleReviewsCount: { type: Type.NUMBER },
            forks: { type: Type.NUMBER },
            durationMinutes: { type: Type.NUMBER }
          },
          required: ["description", "fullDescription", "categories", "isPaid"]
        }
      }
    });

    const response = await Promise.race([
      model,
      new Promise<any>((_, reject) => setTimeout(() => reject(new Error("Gemini API timeout")), 30000))
    ]);
    
    if (!response.text) {
      throw new Error("No text in response from Gemini");
    }
    try {
      const data = JSON.parse(response.text);
      
      // Validation des prix et isPaid
      if (!data.isPaid) {
        data.adultPrice = 0;
        data.child6_12Price = 0;
        data.child3_6Price = 0;
        data.babyPrice = 0;
      } else {
        // S'assurer que les prix sont bien des nombres
        data.adultPrice = typeof data.adultPrice === 'number' ? data.adultPrice : 0;
        data.child6_12Price = typeof data.child6_12Price === 'number' ? data.child6_12Price : 0;
        data.child3_6Price = typeof data.child3_6Price === 'number' ? data.child3_6Price : 0;
        data.babyPrice = typeof data.babyPrice === 'number' ? data.babyPrice : 0;
      }
      
      return data;
    } catch (e) {
      console.error("Failed to parse AI response for site details", e);
      return null;
    }
  } catch (error) {
    if (retries > 0) {
      console.warn(`AI fetch failed for ${siteName}, retrying in 2 seconds... (${retries} retries left)`, error);
      await new Promise(resolve => setTimeout(resolve, 2000));
      return fetchAIDataForSite(siteName, address, currentCategories, retries - 1);
    }
    console.error(`AI fetch failed for ${siteName} after all retries`, error);
    throw error;
  }
}

export async function generateCustomTourProposal(duration: string, theme: string, activities: TouristSite[]) {
  const ai = getAiClient();
  const model = ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: `Génère une proposition de circuit personnalisé.
Durée: ${duration}
Thème: ${theme}
Activités disponibles: ${JSON.stringify(activities.map(a => ({ name: a.name, description: a.description })))}`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          description: { type: Type.STRING },
          itinerary: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                time: { type: Type.STRING },
                activity: { type: Type.STRING },
                description: { type: Type.STRING }
              }
            }
          }
        }
      }
    }
  });

  const response = await model;
  if (!response.text) {
    throw new Error("No text in response from Gemini");
  }
  try {
    return JSON.parse(response.text);
  } catch (e) {
    return null;
  }
}
